<template>
  <div v-on-click-outside="() => (show = false)" class="relative">
    <div
      class="relative flex h-10 items-center justify-between rounded-sm border px-3 py-2 whitespace-nowrap"
      :class="{
        'cursor-pointer': !disabled,
      }"
      :style="{ borderStyle }"
      v-bind="$attrs"
      @click="toggle"
    >
      <div
        v-if="modelValue?.label && modelValue?.color"
        class="flex items-center space-x-2 text-gray-500"
      >
        <div
          class="inline-block h-2 w-2 rounded-full"
          :style="{ backgroundColor: modelValue.color }"
        />
        <span>{{ modelValue.label }}</span>
      </div>
      <span v-else>
        {{ $t("COMMON.STATUS") }}
      </span>

      <icon-base
        v-if="!customOptions"
        class="ml-2"
        :class="{ 'rotate-180 transform': show }"
        height="6"
        width="10"
        :icon="IconDropdownArrow"
        :icon-color="UTIL_COLORS.DEFAULT"
      />
    </div>

    <ul
      v-if="!disabled && show"
      class="absolute left-0 z-10 min-w-max rounded-sm bg-white shadow-sm"
    >
      <li
        v-for="status in options"
        :key="status.label"
        class="selector-dd-list-item"
        @click.stop="handleItemClick(status)"
      >
        <span
          class="mx-2 h-2 w-2 rounded-full"
          :style="{ backgroundColor: status.color }"
        />
        <span>{{ status.label }}</span>
      </li>
      <li
        v-if="!customOptions"
        class="selector-dd-list-item border-t"
        @click.stop="handleItemClick()"
      >
        <icon-base height="16" width="16" :icon="IconGear" class="mx-1" />
        <span>
          {{ $t("SCORECARDS.CREATE_CUSTOM_STATUS") }}
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconGear from "@/components/icons/IconGear.vue";
import { UTIL_COLORS } from "@/helpers/constants";
import { useScorecards } from "@/hooks/scorecards";

import type { IStatusDropdownItem } from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits(["update:modelValue", "customSelected"]);

const props = defineProps({
  modelValue: {
    type: Object as PropType<IStatusDropdownItem | null>,
    required: true,
  },
  customOptions: {
    type: Array as PropType<IStatusDropdownItem[]>,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const { activeScorecard, activeScorecardGroup, isScorecardPage } =
  useScorecards(false);

const options = computed(() => {
  if (props.customOptions) {
    return props.customOptions;
  }

  const statuses =
    (isScorecardPage.value
      ? activeScorecard.value?.statuses
      : activeScorecardGroup.value?.statuses) || [];

  return statuses.map((status) => ({
    label: status.name,
    color: status.color,
  }));
});

const show = ref(false);

const borderStyle = computed(() => {
  if (props.customOptions) {
    return !!props.modelValue?.label || show.value ? "solid" : "dashed";
  }
  return "solid";
});

const toggle = () => {
  if (props.disabled) {
    return;
  }
  show.value = !show.value;
};

const handleItemClick = (item?: IStatusDropdownItem) => {
  if (!item) {
    emit("customSelected");
    show.value = false;
    return;
  }
  emit("update:modelValue", item);
  show.value = false;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.selector-dd-list-item {
  @apply flex cursor-pointer items-center space-x-2 px-2 py-2 whitespace-nowrap text-gray-500 hover:bg-gray-200;
}

.group-rule-status-dd > div {
  @apply h-8 rounded-full border-blue-400 font-semibold text-blue-500;
}
</style>
